import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TableSortLabel,
  TablePagination,
  IconButton,
  Tooltip,
  Button,
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  Typography,
  TextField,
} from "@mui/material";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ArchiveIcon from "@mui/icons-material/Archive";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import HistoryIcon from "@mui/icons-material/History";
import SearchIcon from "@mui/icons-material/Search";
import { FormControlLabel, Checkbox, Menu, MenuItem, ListItemText } from "@mui/material";
import InputBrowser from "./InputBrowser";
import ProjectTagField, { projectPayload, useProjectTags } from "./projects/ProjectTagField";
import UserAvatar from "./components/UserAvatar";
import { useAuthStore } from "./store/authStore";
import { useUserProductAccess } from "./hooks/useUserProductAccess";
import VersionHistoryDialog from "./VersionHistoryDialog";
import { STARTER_TEMPLATES } from "./inputDefinitionTemplates";
import apiClient from "./utils/apiClient";
import PageShell from "./PageShell";
import PageContainer from "./components/PageContainer";
import PageHeader from "./components/PageHeader";
import DataCard, { tableHeaderCellSx } from "./components/DataCard";
import { useInputDefinitionsStore } from "./store/inputDefinitionsStore";
import InputProblemsDialog from "./components/InputProblemsDialog";
import { problemsFromError } from "./utils/inputProblems";

const formatTs = (ms) => {
  if (!ms) return "—";
  try {
    return new Date(ms).toLocaleString();
  } catch {
    return "—";
  }
};

const STATUS_COLORS = {
  draft: "default",
  ready: "success",
  archived: "default",
};

// Compact, consistent vertical rhythm for every body cell — the default
// MUI cell padding reads as oversized once a row carries several stacked
// lines of text (name + source, chip + label, etc.).
const BODY_CELL_SX = { py: 0.75, verticalAlign: "middle" };

// Scope summary — conditions × timepoints, with the analysis-type list
// available on hover so the column stays compact even for large studies.
const scopeSummary = (d) => {
  const conditionCount = Object.keys(d.experimental_conditions || {}).length;
  const dayCount = new Set(
    Object.values(d.time_points || {}).flat()
  ).size;
  return { conditionCount, dayCount };
};

function sortValue(d, orderBy) {
  switch (orderBy) {
    case "name":
      return (d.name || "").toLowerCase();
    case "source":
      return (d.source_filename || "").toLowerCase();
    case "owner":
      return (d.user_id || "").toLowerCase();
    case "product":
      return (d.product || "").toLowerCase();
    case "status":
      return (d.status || "").toLowerCase();
    default:
      return d[orderBy];
  }
}

// Ascending/descending compare with nulls always sorted last, regardless of
// direction — a missing value shouldn't jump to the top just because the
// column is sorted descending.
const getComparator = (order, orderBy) => (a, b) => {
  const av = sortValue(a, orderBy);
  const bv = sortValue(b, orderBy);
  if (av == null && bv == null) return 0;
  if (av == null) return 1;
  if (bv == null) return -1;
  if (av < bv) return order === "desc" ? 1 : -1;
  if (av > bv) return order === "desc" ? -1 : 1;
  return 0;
};

export default function InputLibrary() {
  const navigate = useNavigate();
  const {
    definitions,
    isLoading,
    error,
    fetchDefinitions,
    createBlank,
    updateMeta,
    duplicate,
    deleteDefinition,
    exportXlsx,
    importXlsx,
    launchRun,
  } = useInputDefinitionsStore();
  const userId = useAuthStore((s) => s.user?.id);
  const { hasBioInterpreter } = useUserProductAccess();

  const [deleteId, setDeleteId] = useState(null);
  const [actionError, setActionError] = useState("");
  const [actionInfo, setActionInfo] = useState("");
  // A refused run's input-file problems ({fileName, message, findings}).
  const [runProblems, setRunProblems] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [newName, setNewName] = useState("");
  const fileInputRef = React.useRef(null);
  // "Use in Run" dialog state — keeps the definition we're launching + the run options.
  const [runFor, setRunFor] = useState(null);
  // Empty = the project of the upload the definition came from, if any.
  const projectTags = useProjectTags();
  const [runProject, setRunProject] = useState(null);
  const [runOpts, setRunOpts] = useState({ use_postprocessing: false, use_biointerpreter: false, description: "" });
  const [runSubmitting, setRunSubmitting] = useState(false);
  const [historyFor, setHistoryFor] = useState(null);
  const [search, setSearch] = useState("");
  const [productFilter, setProductFilter] = useState("");
  const [templateMenuAnchor, setTemplateMenuAnchor] = useState(null);
  const [isBrowserOpen, setIsBrowserOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [orderBy, setOrderBy] = useState("modified");
  const [order, setOrder] = useState("desc");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const handleSort = (field) => {
    setOrder((prevOrder) => (orderBy === field && prevOrder === "asc" ? "desc" : "asc"));
    setOrderBy(field);
    setPage(0);
  };

  useEffect(() => {
    fetchDefinitions(true);
  }, [fetchDefinitions]);

  useEffect(() => {
    setPage(0);
  }, [search, productFilter, showArchived]);

  const handleNewBlank = async () => {
    try {
      const name = (newName || "").trim() || "Untitled";
      const created = await createBlank(name);
      setNewName("");
      if (created?.id) {
        navigate(`/input/${created.id}`);
      }
    } catch (e) {
      setActionError(e?.message || "Failed to create definition");
    }
  };

  const handleEdit = (id) => navigate(`/input/${id}`);

  const handleCreateFromTemplate = async (template) => {
    setTemplateMenuAnchor(null);
    setActionError("");
    try {
      const created = await apiClient.post("/input-definition", template.payload);
      if (created?.id) {
        // Optimistically prepend; refresh below picks up the canonical row.
        useInputDefinitionsStore.setState((s) => ({
          definitions: [created, ...s.definitions],
        }));
        navigate(`/input/${created.id}`);
      }
    } catch (e) {
      setActionError(e?.message || "Failed to create from template");
    }
  };

  const handleImportClick = () => fileInputRef.current?.click();

  const handleImportFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = ""; // allow re-selecting the same file
    if (!file) return;
    setActionError("");
    setActionInfo("");
    try {
      const { definition, warnings } = await importXlsx(file);
      if (definition?.id) {
        if (warnings?.length) {
          setActionInfo(`Imported with ${warnings.length} warning(s): ${warnings.slice(0, 3).join("; ")}${warnings.length > 3 ? "…" : ""}`);
        } else {
          setActionInfo("Imported successfully.");
        }
        navigate(`/input/${definition.id}`);
      } else {
        setActionError("Import returned no definition");
      }
    } catch (e) {
      setActionError(e?.message || "Import failed");
    }
  };

  const handleDuplicate = async (id) => {
    try {
      await duplicate(id);
    } catch (e) {
      setActionError(e?.message || "Failed to duplicate");
    }
  };

  const handleArchiveToggle = async (def) => {
    const next = def.status === "archived" ? "draft" : "archived";
    try {
      await updateMeta(def.id, { status: next });
    } catch (e) {
      setActionError(e?.message || "Failed to update status");
    }
  };

  const handleDelete = async () => {
    try {
      await deleteDefinition(deleteId);
    } catch (e) {
      setActionError(e?.message || "Failed to delete");
    } finally {
      setDeleteId(null);
    }
  };

  // --- Bulk actions -------------------------------------------------------

  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const runBulk = async (label, action) => {
    if (selectedIds.size === 0) return;
    setBulkBusy(true);
    setActionError("");
    setActionInfo("");
    const ids = Array.from(selectedIds);
    const errors = [];
    for (const id of ids) {
      try {
        await action(id);
      } catch (e) {
        errors.push(`${id.slice(0, 8)}…: ${e?.message || "failed"}`);
      }
    }
    setBulkBusy(false);
    clearSelection();
    if (errors.length === 0) {
      setActionInfo(`${label} ${ids.length} item${ids.length === 1 ? "" : "s"}.`);
    } else {
      setActionError(
        `${label} completed with ${errors.length} of ${ids.length} failure${errors.length === 1 ? "" : "s"}: ${errors.slice(0, 3).join("; ")}${errors.length > 3 ? "…" : ""}`
      );
    }
    fetchDefinitions(true);
  };

  const handleBulkArchive = () =>
    runBulk("Archived", (id) => updateMeta(id, { status: "archived" }));
  const handleBulkUnarchive = () =>
    runBulk("Unarchived", (id) => updateMeta(id, { status: "draft" }));
  const handleBulkDelete = async () => {
    setBulkDeleteOpen(false);
    await runBulk("Deleted", (id) => deleteDefinition(id));
  };

  const handleOpenRun = (def) => {
    setRunFor(def);
    setRunProject(null);
    setRunOpts({
      use_postprocessing: false,
      use_biointerpreter: false,
      description: def.name || "",
    });
  };
  const handleCloseRun = () => {
    setRunFor(null);
    setRunSubmitting(false);
  };
  const handleConfirmRun = async () => {
    if (!runFor?.id) return;
    setRunSubmitting(true);
    setActionError("");
    setActionInfo("");
    try {
      await launchRun(runFor.id, {
        product: runFor.product,
        use_postprocessing: runOpts.use_postprocessing,
        use_biointerpreter: runOpts.use_biointerpreter,
        description: runOpts.description,
        ...(runProject ? projectPayload(projectTags, runProject) : {}),
      });
      setActionInfo(`Run queued from "${runFor.name}".`);
      handleCloseRun();
      if (userId) {
        navigate(`/report-list/${userId}`);
      }
    } catch (e) {
      const refused = problemsFromError(e);
      if (refused) {
        setRunProblems({ fileName: runFor.name, message: refused.message, findings: refused.findings });
        handleCloseRun();
      } else {
        setActionError(e?.message || "Failed to launch run");
      }
      setRunSubmitting(false);
    }
  };

  const handleExport = async (def) => {
    try {
      const blob = await exportXlsx(def.id);
      if (!blob) {
        setActionError("Export returned no file");
        return;
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safeName = (def.name || "Input").replace(/[^A-Za-z0-9._-]+/g, "_");
      a.download = `${safeName}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      // Refresh to pick up last_exported_at
      fetchDefinitions(true);
    } catch (e) {
      setActionError(e?.message || "Export failed");
    }
  };

  const products = Array.from(
    new Set((definitions || []).map((d) => d.product).filter(Boolean))
  ).sort();
  const needle = search.trim().toLowerCase();
  const visible = (definitions || [])
    .filter((d) => (showArchived ? true : d.status !== "archived"))
    .filter((d) => (productFilter ? d.product === productFilter : true))
    .filter((d) => {
      if (!needle) return true;
      return (
        (d.name || "").toLowerCase().includes(needle) ||
        (d.description || "").toLowerCase().includes(needle) ||
        (d.product || "").toLowerCase().includes(needle)
      );
    });
  const sortedVisible = useMemo(
    () => [...visible].sort(getComparator(order, orderBy)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visible, order, orderBy]
  );
  const pagedVisible = sortedVisible.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );
  const visibleIds = visible.map((d) => d.id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
  const someVisibleSelected = visibleIds.some((id) => selectedIds.has(id));
  const archivedInSelection = visible.some(
    (d) => selectedIds.has(d.id) && d.status === "archived"
  );

  const toggleAllVisible = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        for (const id of visibleIds) next.delete(id);
      } else {
        for (const id of visibleIds) next.add(id);
      }
      return next;
    });
  };

  // Drop selection state for rows that have left the filter window so the
  // bulk-action toolbar can't operate on things the user can't see.
  useEffect(() => {
    setSelectedIds((prev) => {
      const visibleSet = new Set(visibleIds);
      let changed = false;
      const next = new Set();
      for (const id of prev) {
        if (visibleSet.has(id)) next.add(id);
        else changed = true;
      }
      return changed ? next : prev;
    });
    // visibleIds is recomputed each render; depend on the joined string so
    // we don't re-run when the same ids are returned in the same order.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleIds.join("|")]);

  return (
    <PageShell>
      <PageContainer>
        <PageHeader
          icon={<LibraryBooksIcon />}
          title="Input Library"
          subtitle="Your uploaded input files, stored as normalized data. Download as xlsx, edit, or use one in a Run."
          action={
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <TextField
                placeholder="Name new definition…"
                size="small"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                sx={{
                  width: 220,
                  backgroundColor: "background.paper",
                  borderRadius: 1,
                }}
              />
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleNewBlank}
              >
                New Blank
              </Button>
              <Button
                variant="outlined"
                onClick={(e) => setTemplateMenuAnchor(e.currentTarget)}
                sx={{ backgroundColor: "background.paper" }}
              >
                New from template ▾
              </Button>
              <Menu
                anchorEl={templateMenuAnchor}
                open={Boolean(templateMenuAnchor)}
                onClose={() => setTemplateMenuAnchor(null)}
              >
                {STARTER_TEMPLATES.map((t) => (
                  <MenuItem key={t.key} onClick={() => handleCreateFromTemplate(t)}>
                    <ListItemText primary={t.title} secondary={t.description} />
                  </MenuItem>
                ))}
              </Menu>
              <Button
                variant="outlined"
                startIcon={<FileUploadIcon />}
                onClick={handleImportClick}
                sx={{ backgroundColor: "background.paper" }}
              >
                Import xlsx
              </Button>
              <Button
                variant="outlined"
                startIcon={<SearchIcon />}
                onClick={() => setIsBrowserOpen(true)}
                sx={{ backgroundColor: "background.paper" }}
              >
                Browse
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                style={{ display: "none" }}
                onChange={handleImportFileChange}
              />
            </Box>
          }
        />
        {actionError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError("")}>
            {actionError}
          </Alert>
        )}
        {actionInfo && (
          <Alert severity="info" sx={{ mb: 2 }} onClose={() => setActionInfo("")}>
            {actionInfo}
          </Alert>
        )}
        <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <TextField
            size="small"
            placeholder="Search name / description / product…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              minWidth: 280,
              backgroundColor: "background.paper",
              borderRadius: 1,
            }}
          />
          {products.length > 0 && (
            <TextField
              size="small"
              select
              SelectProps={{ native: true }}
              value={productFilter}
              onChange={(e) => setProductFilter(e.target.value)}
              sx={{
                minWidth: 160,
                backgroundColor: "background.paper",
                borderRadius: 1,
              }}
            >
              <option value="">All products</option>
              {products.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </TextField>
          )}
          <Button
            size="small"
            variant="text"
            onClick={() => setShowArchived((s) => !s)}
          >
            {showArchived ? "Hide archived" : "Show archived"}
          </Button>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {visible.length} of {(definitions || []).length} definition{(definitions || []).length === 1 ? "" : "s"}
          </Typography>
        </Box>
        {selectedIds.size > 0 && (
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexWrap: "wrap",
              backgroundColor: "background.paper",
              borderRadius: 1,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {selectedIds.size} selected
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Button
              size="small"
              variant="outlined"
              startIcon={<ArchiveIcon />}
              onClick={handleBulkArchive}
              disabled={bulkBusy}
            >
              Archive
            </Button>
            {archivedInSelection && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<UnarchiveIcon />}
                onClick={handleBulkUnarchive}
                disabled={bulkBusy}
              >
                Unarchive
              </Button>
            )}
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setBulkDeleteOpen(true)}
              disabled={bulkBusy}
            >
              Delete
            </Button>
            <Button size="small" onClick={clearSelection} disabled={bulkBusy}>
              Clear
            </Button>
          </Box>
        )}
        <DataCard noPadding>
          {isLoading && (
            <Box sx={{ p: 3, color: "text.secondary" }}>Loading definitions…</Box>
          )}
          {error && (
            <Box sx={{ p: 3, color: "error.main" }}>Error: {error}</Box>
          )}
          <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...tableHeaderCellSx, width: 48, pr: 0 }} padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={allVisibleSelected}
                    indeterminate={!allVisibleSelected && someVisibleSelected}
                    onChange={toggleAllVisible}
                    inputProps={{ "aria-label": "select all visible" }}
                  />
                </TableCell>
                <TableCell sx={{ ...tableHeaderCellSx, minWidth: 280, py: 1 }}>
                  <TableSortLabel
                    active={orderBy === "name"}
                    direction={orderBy === "name" ? order : "asc"}
                    onClick={() => handleSort("name")}
                  >
                    Name / Source
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ ...tableHeaderCellSx, width: 64, py: 1 }} align="center">
                  <TableSortLabel
                    active={orderBy === "owner"}
                    direction={orderBy === "owner" ? order : "asc"}
                    onClick={() => handleSort("owner")}
                  >
                    Owner
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ ...tableHeaderCellSx, py: 1 }}>
                  <TableSortLabel
                    active={orderBy === "product"}
                    direction={orderBy === "product" ? order : "asc"}
                    onClick={() => handleSort("product")}
                  >
                    Product
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ ...tableHeaderCellSx, py: 1 }}>
                  <TableSortLabel
                    active={orderBy === "status"}
                    direction={orderBy === "status" ? order : "asc"}
                    onClick={() => handleSort("status")}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ ...tableHeaderCellSx, py: 1 }}>Scope</TableCell>
                <TableCell sx={{ ...tableHeaderCellSx, py: 1 }}>
                  <TableSortLabel
                    active={orderBy === "modified"}
                    direction={orderBy === "modified" ? order : "asc"}
                    onClick={() => handleSort("modified")}
                  >
                    Modified
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ ...tableHeaderCellSx, py: 1 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.length === 0 && !isLoading && (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Box sx={{ p: 4, textAlign: "center", color: "text.secondary" }}>
                      No inputs yet. Upload an xlsx from the Reports page or use <strong>Import xlsx</strong> above.
                    </Box>
                  </TableCell>
                </TableRow>
              )}
              {pagedVisible.map((d) => {
                const isArchived = d.status === "archived";
                const isSelected = selectedIds.has(d.id);
                const { conditionCount, dayCount } = scopeSummary(d);
                return (
                  <TableRow
                    key={d.id}
                    hover
                    selected={isSelected}
                    sx={isArchived ? { opacity: 0.6 } : undefined}
                  >
                    <TableCell padding="checkbox" sx={BODY_CELL_SX}>
                      <Checkbox
                        size="small"
                        checked={isSelected}
                        onChange={() => toggleSelected(d.id)}
                        inputProps={{ "aria-label": `select ${d.name || "row"}` }}
                      />
                    </TableCell>
                    <TableCell sx={{ ...BODY_CELL_SX, maxWidth: 420 }}>
                      <Tooltip title={d.name || "Untitled"}>
                        <Box
                          component="button"
                          type="button"
                          onClick={() => handleEdit(d.id)}
                          sx={{
                            display: "block",
                            width: "100%",
                            background: "none",
                            border: 0,
                            p: 0,
                            cursor: "pointer",
                            color: "#1976d2",
                            fontWeight: 500,
                            fontFamily: "inherit",
                            fontSize: "0.85rem",
                            lineHeight: 1.3,
                            textAlign: "left",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {d.name || "Untitled"}
                        </Box>
                      </Tooltip>
                      {d.source_filename ? (
                        <Tooltip title={d.source_filename}>
                          <Typography
                            variant="body2"
                            sx={{
                              display: "block",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              fontFamily:
                                "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
                              fontSize: "0.72rem",
                              lineHeight: 1.4,
                              color: "text.secondary",
                            }}
                          >
                            {d.source_filename}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Chip label="Manual entry" size="small" variant="outlined" sx={{ mt: 0.25, height: 18, fontSize: "0.65rem" }} />
                      )}
                    </TableCell>
                    <TableCell align="center" sx={BODY_CELL_SX}>
                      <UserAvatar email={d.user_id} size={26} />
                    </TableCell>
                    <TableCell sx={BODY_CELL_SX}>{d.product || "—"}</TableCell>
                    <TableCell sx={BODY_CELL_SX}>
                      <Chip
                        label={d.status || "draft"}
                        size="small"
                        color={STATUS_COLORS[d.status] || "default"}
                        variant={isArchived ? "outlined" : "filled"}
                      />
                    </TableCell>
                    <TableCell sx={BODY_CELL_SX}>
                      {conditionCount > 0 ? (
                        <Tooltip
                          title={
                            (d.analysis_types || []).length
                              ? `Analysis types: ${d.analysis_types.join(", ")}`
                              : "No analysis types set"
                          }
                        >
                          <Typography variant="body2" sx={{ color: "text.secondary", whiteSpace: "nowrap" }}>
                            {conditionCount} cond · {dayCount} day{dayCount === 1 ? "" : "s"}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>—</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={BODY_CELL_SX}>
                      <Tooltip
                        title={
                          d.created
                            ? `Created ${formatTs(d.created)}`
                            : ""
                        }
                      >
                        <span>{formatTs(d.modified)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right" sx={BODY_CELL_SX}>
                      <Tooltip title="Edit">
                        <IconButton size="small" color="primary" onClick={() => handleEdit(d.id)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Use in Run">
                        <IconButton size="small" color="success" onClick={() => handleOpenRun(d)}>
                          <PlayArrowIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Download xlsx">
                        <IconButton size="small" onClick={() => handleExport(d)}>
                          <FileDownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Version history">
                        <IconButton size="small" onClick={() => setHistoryFor(d)}>
                          <HistoryIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Duplicate">
                        <IconButton size="small" onClick={() => handleDuplicate(d.id)}>
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={isArchived ? "Unarchive" : "Archive"}>
                        <IconButton size="small" onClick={() => handleArchiveToggle(d)}>
                          {isArchived
                            ? <UnarchiveIcon fontSize="small" />
                            : <ArchiveIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => setDeleteId(d.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={sortedVisible.length}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </DataCard>
      </PageContainer>

      <VersionHistoryDialog
        open={!!historyFor}
        definitionId={historyFor?.id}
        definitionName={historyFor?.name || ""}
        onClose={() => setHistoryFor(null)}
        onRestored={() => fetchDefinitions(true)}
      />

      <InputBrowser
        open={isBrowserOpen}
        onClose={() => setIsBrowserOpen(false)}
        userId={userId}
        onPickDefinition={(def) => {
          if (def?.id) {
            setIsBrowserOpen(false);
            navigate(`/input/${def.id}`);
          }
        }}
        onPickFile={(file) => {
          // The Library auto-creates an input_definition for every upload.
          // Try to open the matching definition's editor; fall back to the
          // Reports list if we can't find a match (older uploads pre-date
          // the auto-create).
          const match = (definitions || []).find(
            (d) => d.source_file_id && d.source_file_id === file.id
          );
          setIsBrowserOpen(false);
          if (match?.id) {
            navigate(`/input/${match.id}`);
          } else if (userId) {
            navigate(`/report-list/${userId}`);
          }
        }}
      />

      {/* Use in Run dialog */}
      <Dialog open={!!runFor} onClose={runSubmitting ? undefined : handleCloseRun} maxWidth="sm" fullWidth>
        <DialogTitle>Run "{runFor?.name}"</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            This will generate the input xlsx from the definition and queue a new Run.
            Product: <strong>{runFor?.product || "MFAlite (default)"}</strong>
          </DialogContentText>
          <TextField
            label="Run description"
            value={runOpts.description}
            onChange={(e) => setRunOpts((s) => ({ ...s, description: e.target.value }))}
            fullWidth
            margin="normal"
            size="small"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={runOpts.use_postprocessing}
                onChange={(_, v) => setRunOpts((s) => ({ ...s, use_postprocessing: v }))}
              />
            }
            label="Run post-processing"
          />
          {hasBioInterpreter && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={runOpts.use_biointerpreter}
                  onChange={(_, v) => setRunOpts((s) => ({ ...s, use_biointerpreter: v }))}
                />
              }
              label="Run BioInterpreter"
            />
          )}
          <ProjectTagField
            value={runProject}
            onChange={setRunProject}
            tags={projectTags.tags}
            available={projectTags.available}
            staff={projectTags.staff}
            projects={projectTags.projects}
            staffHelperText="Leave empty to use the project of the upload this definition came from; you can move the run later."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRun} disabled={runSubmitting}>Cancel</Button>
          <Button onClick={handleConfirmRun} disabled={runSubmitting} variant="contained">
            {runSubmitting ? "Queuing…" : "Queue Run"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)}>
        <DialogTitle>Delete definition?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete <strong>{definitions.find((d) => d.id === deleteId)?.name}</strong>?
            This cannot be undone. Runs already launched from this definition
            keep their input files.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      <InputProblemsDialog
        open={!!runProblems}
        onClose={() => setRunProblems(null)}
        fileName={runProblems?.fileName}
        message={runProblems?.message}
        findings={runProblems?.findings || []}
      />

      <Dialog open={bulkDeleteOpen} onClose={() => setBulkDeleteOpen(false)}>
        <DialogTitle>Delete {selectedIds.size} inputs?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete {selectedIds.size} selected input
            {selectedIds.size === 1 ? "" : "s"}. This cannot be undone. Runs
            already launched from any of these inputs keep their input files.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteOpen(false)}>Cancel</Button>
          <Button onClick={handleBulkDelete} color="error" variant="contained">
            Delete {selectedIds.size}
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
}
